import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AuthSessionFacade } from '@services/auth-session/auth-session.facade';
import { Store } from '@ngxs/store';
import { CLEAR_AUTH_SESSION } from '@store/user/user.action';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const RETRY_HEADER = 'X-SAT-Motor-Auth-Retry';

@Injectable()
export class RequestInterceptorService implements HttpInterceptor {
  errorCodesList1 = [401, 403, 302, '401', '403', '302'];
  errorCodesList2 = [400, 500, 503, '400', '500', '503'];

  constructor(private readonly injector: Injector) { }

  private get authSessionFacade(): AuthSessionFacade {
    return this.injector.get(AuthSessionFacade);
  }

  private get router(): Router {
    return this.injector.get(Router);
  }

  private get store(): Store {
    return this.injector.get(Store);
  }

  intercept(
    request: HttpRequest<any>, next: HttpHandler
  ): Observable<any> {
    const authReq = this.withJsonContentType(this.withCredentials(request));

    return next.handle(authReq).pipe(
      catchError((err) => this.handleError(err, authReq, next))
    );
  }

  private handleError(
    err: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<any> {
    if (this.shouldAttemptRefresh(request, err)) {
      return this.authSessionFacade.refreshSession(true).pipe(
        switchMap((success) => {
          if (!success) {
            return this.handleSessionExpiry(err);
          }

          const retriedRequest = request.clone({
            headers: request.headers.set(RETRY_HEADER, '1'),
          });
          return next.handle(retriedRequest).pipe(
            catchError((retryErr) =>
              this.handleNonRefreshError(retryErr, retriedRequest),
            ),
          );
        }),
        catchError(() => this.handleSessionExpiry(err)),
      );
    }

    return this.handleNonRefreshError(err, request);
  }

  private handleNonRefreshError(
    err: HttpErrorResponse,
    request: HttpRequest<any>,
  ): Observable<never> {
    if (
      this.errorCodesList1.includes(err?.status) &&
      typeof err?.error?.redirectUrl === 'string' &&
      err.error.redirectUrl
    ) {
      this.store.dispatch(new CLEAR_AUTH_SESSION());
      window.location.assign(err.error.redirectUrl);
    } else if (this.shouldNavigateToNotFound(request, err)) {
      this.store.dispatch(new CLEAR_AUTH_SESSION());
      this.router.navigate(['/404']);
    } else if (this.errorCodesList2.includes(err?.status)) {
      console.warn(err);
    }

    return throwError(() => err);
  }

  private handleSessionExpiry(err: HttpErrorResponse): Observable<never> {
    this.store.dispatch(new CLEAR_AUTH_SESSION());

    const redirectUrl =
      this.authSessionFacade.consumeRefreshFailureRedirectUrl() ??
      (typeof err?.error?.redirectUrl === 'string' ? err.error.redirectUrl : null);

    if (redirectUrl) {
      window.location.assign(redirectUrl);
    } else {
      this.router.navigate(['/404']);
    }

    return throwError(() => err);
  }

  private shouldAttemptRefresh(
    request: HttpRequest<any>,
    err: HttpErrorResponse,
  ): boolean {
    if (!this.isBffRequest(request) || this.isAuthEndpoint(request)) {
      return false;
    }

    if (request.headers.has(RETRY_HEADER)) {
      return false;
    }

    if (err?.status !== 401) {
      return false;
    }

    if (typeof err?.error?.redirectUrl === 'string') {
      return false;
    }

    return this.authSessionFacade.hasSession();
  }

  private shouldNavigateToNotFound(
    request: HttpRequest<any>,
    err: HttpErrorResponse,
  ): boolean {
    if (!this.isBffRequest(request) || this.isAuthEndpoint(request)) {
      return false;
    }

    if (![401, 403, '401', '403'].includes(err?.status)) {
      return false;
    }

    return request.headers.has(RETRY_HEADER);
  }

  private withCredentials(request: HttpRequest<any>): HttpRequest<any> {
    if (!this.isBffRequest(request) || request.withCredentials) {
      return request;
    }

    return request.clone({
      withCredentials: true,
    });
  }

  private withJsonContentType(request: HttpRequest<any>): HttpRequest<any> {
    if (
      request.headers.has('Content-Type') ||
      request.body instanceof FormData ||
      request.method === 'GET'
    ) {
      return request;
    }

    return request.clone({
      headers: request.headers.set('Content-Type', 'application/json')
    });
  }

  private isBffRequest(request: HttpRequest<any>): boolean {
    return !!environment.apiUrl && request.url.startsWith(environment.apiUrl);
  }

  private isAuthEndpoint(request: HttpRequest<any>): boolean {
    return (
      request.url.endsWith('/auth/authenticate') ||
      request.url.endsWith('/auth/authenticate/mock') ||
      request.url.endsWith('/auth/refresh') ||
      request.url.includes('/session/logout')
    );
  }
}